import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

// Global timezone state (will be set by TimezoneContext)
let globalTimezone: string | null = null;

/**
 * Sets the global timezone for formatting functions.
 * Called by TimezoneContext when timezone changes.
 */
export function setGlobalTimezone(timezone: string | null) {
  globalTimezone = timezone;
}

/**
 * Gets the effective timezone to use for formatting.
 * Returns the manually selected timezone or the device timezone.
 */
function getEffectiveTimezone(): string {
  if (globalTimezone && globalTimezone !== "auto") {
    return globalTimezone;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

/**
 * Checks if a date has midnight time (placeholder for unknown time)
 */
function isMidnightPlaceholder(date: Date): boolean {
  return date.getUTCHours() === 0 && 
         date.getUTCMinutes() === 0 && 
         date.getUTCSeconds() === 0;
}

/**
 * Formats a date/time string to the user's selected or local timezone.
 * The kickoff_time from the database is stored in UTC, and this function
 * converts it to the user's preferred timezone.
 * 
 * When time is exactly midnight (00:00:00), it's treated as a date-only value
 * and formatted without the time component.
 * 
 * @param dateString - ISO date string (e.g., from database)
 * @param formatPattern - date-fns format pattern (default: "MMM d, HH:mm")
 * @param fallback - Fallback text if date is invalid
 * @returns Formatted string in user's timezone, or fallback text
 */
export function formatLocalTime(
  dateString: string | null | undefined,
  formatPattern: string = "MMM d, HH:mm",
  fallback: string = "TBD"
): string {
  if (!dateString) return fallback;
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return fallback;
    }
    
    const timezone = getEffectiveTimezone();

    // If time is midnight (unknown-time placeholder), show the DATE ONLY and
    // anchor it to UTC so every device shows the same nominal match date
    // (converting an unknown 00:00 UTC to a local zone can shift it a day).
    if (isMidnightPlaceholder(date)) {
      const dateOnlyPattern = formatPattern.includes('HH') || formatPattern.includes('mm')
        ? formatPattern.replace(/[,\s]*HH:mm[,\s]*/g, '').trim() || 'MMM d'
        : formatPattern;
      return formatInTimeZone(date, "UTC", dateOnlyPattern);
    }

    // Use formatInTimeZone for proper timezone conversion so the exact kickoff
    // instant is displayed in each user's own timezone (device-synced).
    return formatInTimeZone(date, timezone, formatPattern);
  } catch (error) {
    // Fallback to basic format if timezone conversion fails
    try {
      const localDate = new Date(dateString);
      if (!isNaN(localDate.getTime())) {
        // If midnight, just show date
        if (isMidnightPlaceholder(localDate)) {
          const dateOnlyPattern = formatPattern.replace(/[,\s]*HH:mm[,\s]*/g, '').trim() || 'MMM d';
          return format(localDate, dateOnlyPattern);
        }
        return format(localDate, formatPattern);
      }
    } catch {
      // ignore
    }
    console.error("Error formatting date:", error);
    return fallback;
  }
}

/**
 * Formats a kickoff time with timezone indicator
 * Shows the time with the user's timezone abbreviation
 */
export function formatLocalTimeWithZone(
  dateString: string | null | undefined,
  formatPattern: string = "MMM d, HH:mm",
  fallback: string = "TBD"
): string {
  if (!dateString) return fallback;
  
  try {
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return fallback;
    }
    
    const timezone = getEffectiveTimezone();
    const formattedTime = formatInTimeZone(date, timezone, formatPattern);
    
    // Get timezone abbreviation
    const tzAbbr = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || '';
    
    return `${formattedTime} ${tzAbbr}`.trim();
  } catch (error) {
    // Fallback
    try {
      const localDate = new Date(dateString);
      if (!isNaN(localDate.getTime())) {
        const formattedTime = format(localDate, formatPattern);
        const tzAbbr = new Intl.DateTimeFormat('en-US', {
          timeZoneName: 'short'
        }).formatToParts(localDate).find(part => part.type === 'timeZoneName')?.value || '';
        return `${formattedTime} ${tzAbbr}`.trim();
      }
    } catch {
      // ignore
    }
    console.error("Error formatting date with timezone:", error);
    return fallback;
  }
}

/**
 * Gets the user's current timezone name
 */
export function getUserTimezone(): string {
  return getEffectiveTimezone();
}

/**
 * Gets the timezone abbreviation for display
 */
export function getTimezoneAbbreviation(): string {
  try {
    const timezone = getEffectiveTimezone();
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(date);
    return parts.find(part => part.type === 'timeZoneName')?.value || timezone;
  } catch {
    return "UTC";
  }
}
